import { useMemo, useState } from "react";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import PackageShowcaseArt from "@/components/showcase/PackageShowcaseArt";
import { getShowcaseVariant } from "@/components/showcase/showcaseUtils";
import Reveal from "@/components/ui/Reveal";
import FAQ from "@/components/ui/FAQ";
import { REAL_PORTFOLIO, BRAND } from "@/constants/assets";
import { wa } from "@/constants/content";
import usePageMeta from "@/hooks/usePageMeta";

const filters = ["All", "Posters", "Reels", "Ads", "Monthly", "Industry"];

export default function Portfolio() {
  usePageMeta({
    title: "Portfolio · 2026 Boards & Case Studies",
    description:
      "Real ThemeStudios 2026 portfolio boards - posters, reels, branding, ads, monthly systems and industry packs at a glance.",
    path: "/portfolio",
  });

  const [active, setActive] = useState("All");
  const filtered = useMemo(
    () => (active === "All" ? REAL_PORTFOLIO : REAL_PORTFOLIO.filter((item) => item.tag === active)),
    [active],
  );

  return (
    <div data-testid="page-portfolio" className="pt-28 md:pt-36">
      <section className="px-6 md:px-12 max-w-[1440px] mx-auto">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-5">
          ✱ 2026 portfolio boards
        </p>
        <h1 className="h1-display text-white text-[8vw] md:text-[4.5vw] max-w-[18ch]">
          The actual <span className="text-[#e03c31]">work</span>. Not mockups.
        </h1>
        <p className="mt-6 max-w-2xl text-sm md:text-[15px] text-zinc-300 leading-relaxed">
          Every tile below is a live ThemeStudios pricing or industry board - the same originals
          shipped to clients. Posters, reels, branding, ads, monthly systems and seven industry
          verticals.
        </p>

        <div className="mt-8 flex items-center gap-2 flex-wrap">
          {filters.map((filter) => (
            <button
              key={filter}
              data-testid={`filter-${filter.toLowerCase()}`}
              onClick={() => setActive(filter)}
              className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.14em] border transition-colors ${
                active === filter
                  ? "bg-[#e03c31] border-[#e03c31] text-white"
                  : "bg-transparent border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
              }`}
            >
              {filter}
            </button>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-10 md:mt-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filtered.map((item, index) => (
            <Reveal key={item.slug} delay={index * 60}>
              <a
                href={`${BRAND.whatsapp}?text=Hi%20ThemeStudios%2C%20I%20saw%20the%20${encodeURIComponent(item.title)}%20board%20on%20your%20site.%20Please%20share%20next%20steps.`}
                target="_blank"
                rel="noreferrer"
                data-testid={`portfolio-item-${index + 1}`}
                className="relative block overflow-hidden rounded-2xl group cursor-pointer bg-[#0a0a0c] border border-white/5 card-lift"
              >
                <div className="tile-media relative overflow-hidden aspect-[3/4] md:aspect-[4/5] bg-[#0a0a0c]">
                  <PackageShowcaseArt
                    image={item.img}
                    variant={getShowcaseVariant(`${item.tag} ${item.slug} ${item.title}`)}
                    layout="portrait"
                    className="absolute inset-0"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 opacity-60 group-hover:opacity-40 transition-opacity" />
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.16em] font-bold bg-white/10 backdrop-blur px-2 py-1 rounded-full">
                      {item.tag}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-200 bg-black/40 px-2 py-1 rounded-full">
                      {item.year}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 backdrop-blur flex items-center justify-center group-hover:bg-[#e03c31] transition-colors">
                    <ArrowUpRight size={14} className="arrow-nudge" />
                  </div>
                </div>
                <div className="p-5 md:p-6">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-1.5">
                    {item.client}
                  </p>
                  <h3 className="font-cabinet text-lg md:text-xl font-bold mb-2 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-zinc-400 text-[13px] leading-relaxed">{item.blurb}</p>
                </div>
              </a>
            </Reveal>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-zinc-500 py-20">No projects in this filter yet.</p>
        )}
      </section>

      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-20">
        <div className="rounded-[2rem] glass p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div>
            <h2 className="h2-section text-2xl md:text-4xl max-w-xl">
              See a board that fits? Start the brief on WhatsApp.
            </h2>
          </div>
          <a
            href={wa("brief")}
            target="_blank"
            rel="noreferrer"
            className="btn-magnetic"
            data-testid="portfolio-whatsapp-cta"
          >
            <MessageCircle size={14} /> Start a Brief
          </a>
        </div>
      </section>

      <FAQ />
    </div>
  );
}
